import type { ReactElement } from 'react';
import { useState } from 'react';
import type { ExperienceMode, TurnOnGender } from '@fetmode/types';
import { TURN_ON_GENDERS } from '@fetmode/types';
import { t } from '@fetmode/i18n';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../auth/index.js';
import { OnboardingChoiceCard } from './OnboardingChoiceCard.js';
import { OnboardingDock } from './OnboardingDock.js';
import { scrollOnboardingToTop, useOnboardingScrollTop } from './onboarding-scroll.js';
import './OnboardingFlow.css';

const MODES: ExperienceMode[] = ['COMMUNITY', 'LIVE', 'VIDEOS'];

function modeTitle(mode: ExperienceMode): string {
  if (mode === 'COMMUNITY') {
    return t('common.experience.communityTitle');
  }
  if (mode === 'VIDEOS') {
    return t('common.experience.videosTitle');
  }
  return t('common.experience.liveTitle');
}

function modeHint(mode: ExperienceMode): string {
  if (mode === 'COMMUNITY') {
    return t('common.experience.communityHint');
  }
  if (mode === 'VIDEOS') {
    return t('common.experience.videosHint');
  }
  return t('common.experience.liveHint');
}

function splitModes(selected: readonly ExperienceMode[]): {
  primary: ExperienceMode;
  secondary: ExperienceMode[];
} | null {
  const primary = selected[0];
  if (!primary) {
    return null;
  }
  return { primary, secondary: selected.slice(1) };
}

export function ExperienceOnboardingPage(): ReactElement {
  const { api, setUser } = useAuth();
  const navigate = useNavigate();
  useOnboardingScrollTop();
  const [selectedModes, setSelectedModes] = useState<ExperienceMode[]>(['LIVE']);
  const [genders, setGenders] = useState<TurnOnGender[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleMode = (mode: ExperienceMode): void => {
    setSelectedModes((current) =>
      current.includes(mode) ? current.filter((entry) => entry !== mode) : [...current, mode],
    );
  };

  const toggleGender = (gender: TurnOnGender): void => {
    setGenders((current) =>
      current.includes(gender) ? current.filter((entry) => entry !== gender) : [...current, gender],
    );
  };

  const persist = async (modes: ExperienceMode[]): Promise<void> => {
    const split = splitModes(modes);
    if (!split) {
      setError(t('common.experience.modesRequired'));
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      await api.updateExperience({
        primaryExperienceMode: split.primary,
        secondaryExperienceModes: split.secondary,
      });
      await api.updateTurnOns({ genders });

      const updatedUser = await api.getMe();
      setUser(updatedUser);

      scrollOnboardingToTop();
      void navigate('/onboarding/turn-ons', { replace: true });
    } catch {
      setError(t('common.experience.error'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <section className="onboarding-flow">
      <h1 className="onboarding-flow__title">{t('common.experience.title')}</h1>
      <p className="onboarding-flow__subtitle">{t('common.experience.subtitle')}</p>

      <section className="onboarding-flow__section" aria-labelledby="experience-interests-title">
        <h2 id="experience-interests-title" className="onboarding-flow__section-title">
          {t('common.experience.interestsTitle')}
        </h2>
        <p className="onboarding-flow__section-hint">{t('common.experience.interestsHint')}</p>
        <div
          className="onboarding-flow__grid onboarding-flow__grid--flush"
          role="group"
          aria-labelledby="experience-interests-title"
        >
          {MODES.map((mode) => (
            <OnboardingChoiceCard
              key={mode}
              imageSeed={`mode-${mode.toLowerCase()}`}
              label={modeTitle(mode)}
              hint={modeHint(mode)}
              selected={selectedModes.includes(mode)}
              disabled={isSaving}
              onClick={() => {
                toggleMode(mode);
              }}
            />
          ))}
        </div>
      </section>

      <section className="onboarding-flow__section" aria-labelledby="experience-genders-title">
        <h2 id="experience-genders-title" className="onboarding-flow__section-title">
          {t('common.experience.gendersTitle')}
        </h2>
        <p className="onboarding-flow__section-hint">{t('common.experience.gendersSubtitle')}</p>
        <ul className="onboarding-flow__grid onboarding-flow__grid--flush">
          {TURN_ON_GENDERS.map((gender) => (
            <li key={gender}>
              <OnboardingChoiceCard
                imageSeed={`gender-${gender}`}
                label={t(`common.turnOns.gender.${gender}`)}
                selected={genders.includes(gender)}
                disabled={isSaving}
                onClick={() => {
                  toggleGender(gender);
                }}
              />
            </li>
          ))}
        </ul>
      </section>

      {error ? <p className="fm-error">{error}</p> : null}

      <OnboardingDock
        selectedCount={selectedModes.length + genders.length}
        continueLabel={isSaving ? t('common.experience.saving') : t('common.experience.continue')}
        continueDisabled={isSaving || selectedModes.length === 0}
        skipDisabled={isSaving}
        onContinue={() => {
          void persist(selectedModes);
        }}
        onSkip={() => {
          void persist(selectedModes.length > 0 ? selectedModes : ['LIVE']);
        }}
      />
    </section>
  );
}
